// Libraries
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import fileDownload from 'js-file-download';
import moment from 'moment';

// MUI
import Grid from '@mui/material/Grid';

// Api
import contractsApi from '../../../api/contractsApi';

// Core
import { ResourceTextApplication } from '../../../core/resources';
import FormValidator from '../../../core/FormValidator';

// Feature
import { IContract, IContractListItem } from '../../../interfaces/IContract';
import ContractsList from './ContractsList';
import { ASwitch } from '../../../common/inputs';
import { IContactPerson, IFarmDetails } from '../../../interfaces/IBusinessEntities';
import ViewLoader from '../../../common/ViewLoader';
import BeginSigningProcessDialog from '../dialog/BeginSigningProcessDialog';
import TerminateContractDialog from '../dialog/TerminateContractDialog';
import ContractDialog from '../dialog/ContractDialog';
import PdfDialog, { PdfDialogProps } from '../dialog/PdfDialog';
import { AAddButton } from '../../../common/buttons';
import companyApi from '../../../api/companyApi';
import accessManagementApi from '../../../api/accessManagementApi';
import { AnelmaContractStatusEnum } from '../../../interfaces/enums';
import authorization from '../../../core/authorization';
import { getProducerRoleForDifferentEnvironments } from '../dialog/ProducerRoleHelper';

export interface IContractListViewProps {
    farm: IFarmDetails;
    contracts: IContractListItem[];
    getContracts: (farm: IFarmDetails) => Promise<void>;
    producerView?: boolean;
}

export default function ContractListView(props: IContractListViewProps) {
    const { farm, contracts, getContracts, producerView } = props;

    const { t } = useTranslation<ResourceTextApplication[]>(['AnelmaGeneral', 'AnelmaContract']);
    const { enqueueSnackbar } = useSnackbar();
    const validator = new FormValidator();

    const [loading, setLoading] = useState<boolean>(true);

    const [filteredContracts, setFilteredContracts] = useState<IContractListItem[]>([]);
    const [showExpired, setShowExpired] = useState<boolean>(false);

    const [snellmanContactPersons, setSnellmanContactPersons] = useState<IContactPerson[]>([]);
    const [contractBlob, setContractBlob] = useState<string>('');
    const [pdfDialogContractNumber, setPdfDialogContractNumber] = useState<string>('');
    const [pdfDialogContractTitle, setPdfDialogContractTitle] = useState<string>('');
    const [pdfDialogReadonly, setPdfDialogReadonly] = useState<boolean | undefined>();
    const [contractNumberToSign, setContractNumberToSign] = useState<string>('');
    const [contractToTerminate, setContractToTerminate] = useState<IContractListItem | null>(null);

    const [showNewContractDialog, setShowNewContractDialog] = useState<boolean>(false);
    const [showPdfDialog, setShowPdfDialog] = useState<boolean>(false);
    const [pdfDialogProps, setPdfDialogProps] = useState<PdfDialogProps | null>(null);
    const [showConfirmSigningDialog, setShowConfirmSigningDialog] = useState<boolean>(false);
    const [showTerminateDialog, setShowTerminateDialog] = useState<boolean>(false);

    useEffect(() => {
        if (producerView) {
            setLoading(false);
        } else {
            getSnellmanContactPersons().then((persons) => {
                setSnellmanContactPersons(persons);
                setLoading(false);
            });
        }
    }, [producerView]);

    useEffect(() => {
        setPdfDialogProps({
            pdfUrl: contractBlob,
            onClose: pdfDialogReadonly ? readonlyPdfDialogOnClose : pdfDialogOnClose,
            contractNumber: pdfDialogContractNumber,
            title: pdfDialogContractTitle,
            readonly: pdfDialogReadonly,
        });
    }, [pdfDialogContractNumber, pdfDialogContractTitle, contractBlob, pdfDialogReadonly]);

    useEffect(() => {
        // set contract data rows to show/hide expired and redundant
        setFilteredContracts(
            contracts.filter((c) => {
                if (showExpired) {
                    return c.Status === AnelmaContractStatusEnum.Ended;
                } else {
                    return c.Status !== AnelmaContractStatusEnum.Ended;
                }
            })
        );
    }, [contracts, showExpired]);

    const refreshList = () => {
        setLoading(true);
        getContracts(farm).then(() => setLoading(false));
    };

    const getSnellmanContactPersons = async () => {
        let adminCompany = await companyApi.getCompanyFullDetailsByName(
            'Snellmanin Lihanjalostus Oy'
        );

        if (!adminCompany) {
            enqueueSnackbar(t('AnelmaGeneral:1020'), {
                variant: 'error',
            });
        } else {
            var persons = adminCompany.Items[0].ContactPersons;

            let adminMembers = await accessManagementApi.getRoleMembers([
                'Snellman Anelma - Allekirjoitusoikeus - Snellman',
            ]);

            if (!adminMembers) {
                enqueueSnackbar(t('AnelmaGeneral:1020'), {
                    variant: 'error',
                });
            } else {
                persons = persons.filter(
                    (p) =>
                        adminMembers?.Items[0].userMembers.findIndex(
                            (m) => m.username === p.Logins[0].Login
                        ) !== -1
                );

                return persons;
            }
        }

        return [];
    };

    const createNewContract = () => {
        const producerRoleName = getProducerRoleForDifferentEnvironments();

        // No contact persons at all
        if (!farm.ContactPersons || farm.ContactPersons.length === 0) {
            enqueueSnackbar(t('AnelmaContract:1047'), { variant: 'error' });
            return;
        }

        // Filter only those contact persons who have the producer role
        const producerContacts = farm.ContactPersons.filter((cp) =>
            cp.Roles?.some((role) => role.Name.includes(producerRoleName))
        );

        // Display error message when no producer contact persons found, described in the ticket AN-2693
        if (producerContacts.length === 0) {
            enqueueSnackbar(t('AnelmaContract:1047'), { variant: 'error' });
            return;
        }

        setShowNewContractDialog(true);
    };

    const saveContract = (contract: IContract) => {
        contractsApi.createContract(contract).then((response) => {
            if (!response) {
                enqueueSnackbar(t('AnelmaGeneral:1019'), {
                    variant: 'error',
                });
            } else {
                contractsApi.getContractPdf(response.Entity.Id).then((file) => {
                    if (file) {
                        var url = URL.createObjectURL(file);
                        setContractBlob(url);
                        setPdfDialogContractNumber(response.Entity.Id);
                        setPdfDialogContractTitle(response.Entity.Title);

                        setShowPdfDialog(true);
                    }
                });
            }
        });
    };

    const openContract = (contractNumber: string, contractTitle: string, terminate: boolean) => {
        contractsApi.getContractPdf(contractNumber).then((response) => {
            if (!response) {
                enqueueSnackbar(t('AnelmaGeneral:1020'), {
                    variant: 'error',
                });
            } else {
                var url = URL.createObjectURL(response);
                setContractBlob(url);
                setPdfDialogContractNumber(contractNumber);
                setPdfDialogReadonly(true);
                setPdfDialogContractTitle(contractTitle);
                if (terminate) {
                    setShowTerminateDialog(true);
                } else {
                    setShowPdfDialog(true);
                }
            }
        });
    };

    const downloadContract = (contractNumber: string, title: string) => {
        contractsApi.getContractPdf(contractNumber).then((response) => {
            if (!response) {
                enqueueSnackbar(t('AnelmaGeneral:1020'), {
                    variant: 'error',
                });
            } else {
                fileDownload(
                    response,
                    title + ' ' + contractNumber + ' - ' + moment().format('DD-MM-yyyy') + '.pdf'
                );
            }
        });
    };

    const beginSignContract = (contractNumber: string) => {
        setShowConfirmSigningDialog(true);
        setContractNumberToSign(contractNumber);
    };

    const confirmBeginSigningProcess = (confirmed: boolean) => {
        if (confirmed && contractNumberToSign.length > 0)
            contractsApi.startContractSignProcess(contractNumberToSign).then((response) => {
                if (!response) {
                    enqueueSnackbar(t('AnelmaGeneral:1020'), {
                        variant: 'error',
                    });
                } else {
                    enqueueSnackbar(t('AnelmaContract:1056'), {
                        variant: 'success',
                    });
                }

                refreshList();
            });
        setShowConfirmSigningDialog(false);
        setContractNumberToSign('');
    };

    const beginTerminateContract = (contract: IContractListItem) => {
        openContract(contract.Signatories[0].ContractId, contract.Title, true);
        setContractToTerminate(contract);
        setShowTerminateDialog(true);
    };

    const terminateContract = async (terminationDate: Date, terminatedBySnellman: boolean) => {
        if (contractToTerminate) {
            let response = await contractsApi.terminateContract(
                contractToTerminate.Id,
                terminationDate,
                terminatedBySnellman
            );

            if (!response) {
                enqueueSnackbar(t('AnelmaGeneral:1020'), {
                    variant: 'error',
                });
            } else {
                enqueueSnackbar(t('AnelmaGeneral:1018'), {
                    variant: 'success',
                });
            }
        }
    };

    const terminateDialogOnClose = (confirmed: boolean) => {
        setShowTerminateDialog(false);
        setContractToTerminate(null);

        if (confirmed) refreshList();
    };

    const deleteContract = async (contractNumber: string) => {
        await contractsApi.deleteContract(contractNumber).then((response) => {
            if (!response) {
                enqueueSnackbar(t('AnelmaGeneral:1020'), {
                    variant: 'error',
                });
            }
        });
    };

    const pdfDialogOnClose: (confirmed: boolean) => void = (confirmed) => {
        if (confirmed) {
            readonlyPdfDialogOnClose();
            setShowNewContractDialog(false);

            enqueueSnackbar(t('AnelmaGeneral:1018'), {
                variant: 'success',
            });

            refreshList();
        } else {
            deleteContract(pdfDialogContractNumber).then(() => {
                readonlyPdfDialogOnClose();
            });
        }
    };

    const readonlyPdfDialogOnClose = () => {
        setShowPdfDialog(false);

        setPdfDialogContractNumber('');
        setContractBlob('');
        setPdfDialogReadonly(undefined);
    };

    const isNewContractBtnDisabled = (): boolean => {
        if (authorization.canCreateAdminContract) {
            return false;
        }
        return true;
    };

    return loading || !farm ? (
        <ViewLoader />
    ) : (
        <>
            <Grid container style={{ marginTop: '10px' }}>
                <Grid size={{ sm: 6 }}>
                    <ASwitch
                        checked={showExpired}
                        id='show-expired-and-redundant'
                        label={t('AnelmaContract:1001')}
                        onChange={(v) => setShowExpired(v)}
                        validator={validator}
                        withoutContainer
                    ></ASwitch>
                </Grid>

                <Grid size={{ sm: 6 }} style={{ textAlign: 'right' }}>
                    {props?.producerView || (
                        <AAddButton
                            onClick={createNewContract}
                            disabled={isNewContractBtnDisabled()}
                        >
                            {t('AnelmaContract:1002')}
                        </AAddButton>
                    )}
                </Grid>
            </Grid>

            <ContractsList
                contracts={filteredContracts}
                openContract={openContract}
                downloadContract={(contractNumber, title) =>
                    downloadContract(contractNumber, title)
                }
                signContract={beginSignContract}
                terminateContract={beginTerminateContract}
                showExpired={showExpired}
                producerView={producerView}
            ></ContractsList>

            {showNewContractDialog && farm && (
                <ContractDialog
                    farm={farm}
                    snellmanContactPersons={snellmanContactPersons}
                    validator={new FormValidator()}
                    saveContract={saveContract}
                    onClose={() => setShowNewContractDialog(false)}
                ></ContractDialog>
            )}

            {showPdfDialog && pdfDialogProps && <PdfDialog {...pdfDialogProps} />}

            {showConfirmSigningDialog && (
                <BeginSigningProcessDialog onClose={confirmBeginSigningProcess} />
            )}

            {showTerminateDialog && contractToTerminate && (
                <TerminateContractDialog
                    pdfUrl={pdfDialogProps?.pdfUrl || ''}
                    title={pdfDialogProps?.title || ''}
                    contract={contractToTerminate}
                    terminateContract={terminateContract}
                    onClose={terminateDialogOnClose}
                />
            )}
        </>
    );
}
